import React from 'react';

import { formatNumber } from '../ListingSources/format';

export const OUTCOME_FAILED = 'failed';

// The five answers a run gives about a property, in the order the cards read.
export const OUTCOMES = ['updated', 'created', 'review', 'skipped', OUTCOME_FAILED];

export const OUTCOME_LABEL = {
    updated: 'Linked',
    created: 'New investor',
    review: 'Needs review',
    skipped: 'Left alone',
    failed: 'Failed',
};

// The listing design's colour each answer borrows: a review is amber like the
// crawler's "left alone", because someone may want to look; leaving a property
// alone here is grey like its "unchanged".
const OUTCOME_STYLE = {
    updated: 'updated',
    created: 'created',
    review: 'skipped',
    skipped: 'unchanged',
    failed: 'failed',
};

export function OutcomePill({ outcome }) {
    const style = outcome === OUTCOME_FAILED ? 'failed-outcome' : OUTCOME_STYLE[outcome];
    return <span className={'ls-cellpill ls-cellpill--' + style}>{OUTCOME_LABEL[outcome] || outcome}</span>;
}

// Where a failed property's check stopped, as the run files it under the item's "failed" note.
export const FAILED_STAGE_LABEL = {
    lightbox: 'LightBox',
    kore: 'Kore',
    ai: 'AI',
    database: 'Database',
    save: 'Save',
};

// The run's own notes: its failures by stage, and the wait it is in for a service
// that stopped answering. The latest of a kind: every wait is a note of its own.
function runNote(run, key) {
    const notes = (run.notes || []).filter((note) => note && note[key]);
    return notes.length ? notes[notes.length - 1] : null;
}

// The stage most of a run's failures stopped at, if it failed anywhere.
export function dominantFailedStage(run) {
    const note = runNote(run, 'failed');
    const stages = note ? Object.keys(note.failed) : [];
    return stages.length ? stages.reduce((best, stage) => (note.failed[stage] > note.failed[best] ? stage : best)) : null;
}

// The wait a running run is in, while it lasts.
export function waitingNote(run) {
    const note = runNote(run, 'wait');
    return run.status === 'running' && note && Date.parse(note.wait.until) > Date.now() ? note : null;
}

export function FailedStagePill({ item }) {
    const note = noteOf(item, 'failed');

    if (!note) return null;

    return <span className="ls-cellpill ls-cellpill--mode">{FAILED_STAGE_LABEL[note.value] || note.value}</span>;
}

export function CountChips({ run, large }) {
    const dry = run.mode === 'dry_run';
    const stage = dominantFailedStage(run);

    return (
        <div className={'ls-counts' + (large ? ' ls-counts--large' : '')}>
            {OUTCOMES.map((key) => {
                const value = run[key] || 0;
                const className = [
                    'ls-count',
                    'ls-count--' + OUTCOME_STYLE[key],
                    value ? '' : 'is-zero',
                    dry ? 'is-dry' : '',
                ].filter(Boolean).join(' ');
                const label = key === OUTCOME_FAILED && value && stage
                    ? OUTCOME_LABEL[key] + ' · ' + (FAILED_STAGE_LABEL[stage] || stage)
                    : OUTCOME_LABEL[key];

                return (
                    <div key={key} className={className}>
                        <div className="ls-count__value">{formatNumber(value)}</div>
                        <div className="ls-count__label">{label}</div>
                    </div>
                );
            })}
        </div>
    );
}

export function WaitingStrip({ run }) {
    const note = waitingNote(run);

    if (!note) return null;

    return (
        <div className="ls-alert ls-alert--warn or-waiting">
            <span className="ls-alert__text">{note.text}</span>
        </div>
    );
}

// What a run item says under one column of its notes, if anything.
export function noteOf(item, column) {
    return (item.notes || []).filter((note) => note.column === column)[0] || null;
}

export const REVIEW_KIND_LABEL = {
    kore: 'Kore choice',
    parcel: 'Parcel',
    pick: 'Owner choice',
    held: 'In iCLIMB',
    owned: 'Owner on file',
    failed: 'Failed',
};

export function CheckIcon({ size }) {
    return (
        <svg width={size || 11} height={size || 11} viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12.5 L9.5 17 L19 7.5" />
        </svg>
    );
}

export function CrossIcon() {
    return (
        <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            <path d="M3.5 3.5 L8.5 8.5 M8.5 3.5 L3.5 8.5" />
        </svg>
    );
}
